import { ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { provideSaphirAuth, saphirTokenInterceptor } from '@saphir/auth-client';
// Static import of a token and three interceptors from the module package:
// `sideEffects: false` lets the bundler drop everything else, so the module
// itself stays lazy.
import {
  FAKT_API_BASE_URL,
  fibuApiInterceptor,
  materialApiInterceptor,
  schreibvorgangInterceptor,
} from '@saphir/fakt-module';
// The fleet backend base-URL token, bound below from this environment.
import { FLEET_API_BASE_URL } from '@saphir/fleet-module';
// The Calendar backend base-URL token, bound below from this environment.
import { CALENDAR_API_BASE_URL } from '@saphir/calendar-module';
import { providePrimeNG } from 'primeng/config';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { provideModuleContext } from './core/context/module-context.providers';
import {
  provideStaleBuildDetection,
  staleBuildNavigationErrorHandler,
} from './core/errors/stale-build.providers';
import { apiUrlInterceptor } from './core/http/api-url.interceptor';
import { authTokenInterceptor } from './core/http/auth-token.interceptor';
import { companyIdInterceptor } from './core/http/company-id.interceptor';
import { allModuleApiBases, moduleApiBase } from './core/http/module-api';
import { provideI18n } from './core/i18n/i18n.providers';
import { provideModuleRegistry } from './core/modules/module-registry.providers';
import { SaphirPreset } from './core/theme/saphir-preset';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // A lazy chunk that no longer exists after a redeploy is reported as a
    // stale build (see `StaleBuildService`); errors are still logged as before.
    provideStaleBuildDetection(),

    provideModuleRegistry(),

    provideRouter(
      routes,
      // Binds route params and `data` entries to component inputs of the same name.
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withNavigationErrorHandler(staleBuildNavigationErrorHandler),
    ),

    // Registered once for the whole application, modules included — a module
    // that injects `HttpClient` gets these without configuring anything.
    // Order matters: the URL is resolved first, then the token is attached to
    // the requests that turned out to target our own API. Which token
    // interceptor runs depends on the auth mode: the library's one carries the
    // real token and keeps it fresh; the mock one attaches the fake bearer.
    // The three Fakt interceptors belong to `@saphir/fakt-module`, but HTTP
    // interceptors can only be registered where `HttpClient` is — here, at the
    // root; a module cannot add them from its routes. Without them the module's
    // backend errors (400/403/409/5xx) surfaced as toasts only in its standalone
    // app and were swallowed silently inside the shell, and its sidebar counters
    // never refreshed after a save. They only react to requests the module marks
    // (`MATERIAL_API` / `FIBU_API` context) or to successful writes, so every
    // other module's traffic passes through untouched.
    provideHttpClient(
      withInterceptors([
        apiUrlInterceptor,
        environment.auth.useMockAuth ? authTokenInterceptor : saphirTokenInterceptor,
        companyIdInterceptor,
        materialApiInterceptor,
        fibuApiInterceptor,
        schreibvorgangInterceptor,
      ]),
    ),

    // Sign-in through Saphir Identity. Configured even in mock mode — the
    // config token is inert without the guard, callback and interceptor, and
    // providing it unconditionally keeps the provider list identical in both
    // modes.
    provideSaphirAuth({
      idpBaseUrl: environment.auth.idpBaseUrl,
      clientId: environment.auth.clientId,
      redirectUri: environment.auth.redirectUri,
      // The only hosts the access token may be attached to: every module
      // backend and UMC. The Identity host is deliberately not here — it
      // manages its own auth.
      apiBaseUrls: [...allModuleApiBases(), environment.umc.baseUrl],
    }),

    ...provideI18n(),

    // The shell owns the theme. Modules consume it through the design tokens and
    // must never call `providePrimeNG` themselves.
    providePrimeNG({
      theme: {
        preset: SaphirPreset,
        // A class instead of the default `system`, so the switch in the header
        // can override what the operating system asks for. `ThemeService` puts
        // it on the root element.
        options: { darkModeSelector: '.app-dark' },
      },
      ripple: true,
    }),

    provideModuleContext(),

    // The accounting module addresses its backend by its own token rather than
    // by relative URLs; it is fed from the same per-module map. Without an entry
    // the module falls back to its local-development default.
    ...(moduleApiBase('fakt')
      ? [{ provide: FAKT_API_BASE_URL, useValue: moduleApiBase('fakt') }]
      : []),

    // The fleet module addresses its backend by its own token as well, from the
    // same map. Without an entry it falls back to its relative (proxied) default.
    ...(moduleApiBase('fleet')
      ? [{ provide: FLEET_API_BASE_URL, useValue: moduleApiBase('fleet') }]
      : []),

    // The Calendar module addresses its backend by its own token, from the same
    // map; it sets X-Company-Id itself. Without an entry it uses its local default.
    ...(moduleApiBase('calendar')
      ? [{ provide: CALENDAR_API_BASE_URL, useValue: moduleApiBase('calendar') }]
      : []),
  ],
};
